import * as React from 'react';
import { useCallback } from 'react';
import { ApolloProvider } from '@apollo/react-hooks';

import SeatMapService from './SeatMapService/SeatMapService';
import { SeatSelectHandler, Seat } from '../types';
import { useGraphQLClient } from '../../graphql-client';
import { RunServiceRequest, RunServiceRequestPassengers, Segment } from '../../Checkout/types';
import { PassengerState } from '../../Checkout/store/passengers/reducers';
import { fillOrder } from '../../Checkout/store/order/actions';
import { getSelectedServices, updateSeatServices } from '../utils';
import SimpleLoader from '../../SimpleLoader';
import { useRenderers } from '../../renderProps';
import { SelectedExtraSeat } from '../../BaggageService/types';
import {
	FlightSeatMapFragment,
	OrderAdditionalServicesFragment,
	PassengerFragment,
	SeatMapRestrictions
} from '@websky/graphql';

export interface Props {
	className?: string;
	orderId?: string;
	isNewOrder?: boolean;
	segmentId?: number;
	passengers: PassengerState[];
	travellers?: PassengerFragment[];
	runServiceRequest?: RunServiceRequest;
	seatMap?: FlightSeatMapFragment;
	additionalServices: OrderAdditionalServicesFragment;
	segmentIndex?: number;
	passengerIndex?: number;
	onProceed: () => void;
	setSegment?: (value: number) => void;
	setPassenger?: (value: number) => void;
	fullScreen?: boolean;
	fillOrder?: typeof fillOrder;
	showRecommendations?: boolean;
	onConfirmRecommendation?: () => void;
	segments?: Segment[];
	selectedExtraSeats?: SelectedExtraSeat[];
	setSeatCallback?: (seats: Seat[], passengerId: string, segmentId: string) => void;
	setClearSeatCallback?: (seats: Seat[], passengerId: string, segmentId: string) => void;
	onSegmentClear?: (segmentId?: number) => void;
	onPassengerClear?: (passengerId: string) => void;
	isLoading?: boolean;
	isReturnFlight?: boolean;
	onClose?: () => void;
	confirmRecommendedSeatsWithoutDialog?: boolean;
	dialogRef?: React.RefObject<HTMLDivElement>;
	activeFilters?: SeatMapRestrictions[];
}

const SeatMapComponentStore: React.FunctionComponent<Props> = React.memo(
	({
		className,
		orderId,
		passengers,
		seatMap,
		additionalServices,
		segmentId,
		segmentIndex,
		passengerIndex,
		runServiceRequest,
		onProceed,
		setSegment,
		setPassenger,
		segments,
		selectedExtraSeats,
		fillOrder,
		showRecommendations,
		travellers,
		fullScreen = true,
		setSeatCallback,
		setClearSeatCallback,
		onSegmentClear,
		onPassengerClear,
		isLoading,
		confirmRecommendedSeatsWithoutDialog,
		onClose,
		isNewOrder,
		dialogRef,
		isReturnFlight,
		activeFilters
	}) => {
		const { SeatMapService: SeatMapServiceOverride = SeatMapService } = useRenderers();

		const seatSelectHandler: SeatSelectHandler = useCallback(
			async (passengerId, seats, segmentId) => {
				const currentPassenger = passengers.find(orderPassenger => orderPassenger.id === passengerId);

				if (runServiceRequest) {
					runServiceRequest(
						updateSeatServices(passengers, seatMap.segments[segmentId], currentPassenger.id, seats)
					);
				} else {
					setSeatCallback(seats, currentPassenger.id, seatMap.segments[segmentId].segmentsInfo.id);
				}
			},
			[runServiceRequest, passengers, seatMap]
		);

		const clearSeatHandler: SeatSelectHandler = useCallback(
			async (passengerId, seats, segmentId) => {
				const currentPassenger = passengers.find(orderPassenger => orderPassenger.id === passengerId);

				if (runServiceRequest) {
					runServiceRequest(
						updateSeatServices(passengers, seatMap.segments[segmentId], currentPassenger.id, seats)
					);
				} else {
					setClearSeatCallback(seats, currentPassenger.id, segmentId.toString());
				}
			},
			[runServiceRequest, passengers, seatMap]
		);

		// do not make two request per passenger in order to clear segment seats
		const clearOnSegment = useCallback(
			(segmentIndex: number) => {
				if (runServiceRequest) {
					const passengersList: RunServiceRequestPassengers[] = [];
					const currentSegmentId = seatMap.segments[segmentIndex].segmentsInfo.id;

					passengers.forEach((passenger, passengerIndex) => {
						passengersList.push({
							passengerId: passenger.id,
							seats: getSelectedServices(passenger, seatMap.segments[segmentIndex], passengerIndex, null)
								.filter(seat => seat?.isConfirmed || seat.segmentId !== currentSegmentId)
								.map(seat => ({
									row: parseInt(seat.row),
									letter: seat.letter,
									isConfirmed: seat.isConfirmed,
									isChangeable: seat.isChangeable,
									onRequest: seat.onRequest,
									segment: {
										id: seat.id
									},
									seat: null,
									product: null,
									type: seat.seatType
								}))
						});
					});

					runServiceRequest(passengersList);
				} else {
					onSegmentClear(segmentIndex);
				}
			},

			[runServiceRequest, passengers, seatMap]
		);

		return seatMap ? (
			<>
				{isLoading && <SimpleLoader />}

				<SeatMapServiceOverride
					{...{
						className,
						clearOnSegment,
						passengers,
						seatMaps: seatMap,
						additionalServices,
						passengerIndex,
						segmentIndex,
						onProceed,
						setSegment,
						setPassenger,
						onSelect: seatSelectHandler,
						fullScreen,
						onCancelSeat: clearSeatHandler,
						travellers,
						seatRecommendEnabled: showRecommendations && !!segments && !!travellers && !!fillOrder,
						segments,
						selectedExtraSeats,
						orderId,
						runServiceRequest,
						setSeatCallback,
						confirmRecommendedSeatsWithoutDialog,
						onClose,
						onPassengerClear,
						isNewOrder,
						dialogRef,
						isReturnFlight,
						segmentId,
						activeFilters
					}}
				/>
			</>
		) : null;
	}
);

const SeatMapWrapperWithGql: React.FunctionComponent<Props> = props => {
	const client = useGraphQLClient();

	return (
		<ApolloProvider client={client}>
			<SeatMapComponentStore {...props} />
		</ApolloProvider>
	);
};

export default SeatMapWrapperWithGql;
